import { memo, useMemo } from 'react';

import { type BreadcrumbsItemType } from '@air/component-breadcrumbs';

import { AssetModalBreadcrumbs } from '~/components/AssetModalV2/Header/components/AssetModalBreadcrumbs';
import { AssetModalHeaderSeparator } from '~/components/AssetModalV2/Header/components/AssetModalHeaderSeparator';
import { AssetModalVersionsButton } from '~/components/AssetModalV2/Header/components/AssetModalVersionsButton';
import { Header } from '~/components/AssetModalV2/Header/components/Header';
import { PublicAssetTitleBreadcrumbContent } from '~/components/AssetModalV2/Header/components/PublicAssetTitleBreadcrumbContent';
import { PublicAssetShareButton } from '~/components/AssetModalV2/Header/PublicAssetComponents/PublicAssetShareButton';
import { PublicBanner } from '~/components/AssetModalV2/Header/PublicAssetComponents/PublicBanner';
import { PublicOptionsMenu } from '~/components/AssetModalV2/Header/PublicAssetComponents/PublicOptionsMenu';
import { useAssetTitleBreadcrumbItem } from '~/components/AssetModalV2/hooks/useAssetTitleBreadcrumbItem';
import { useCanViewAllVersionsOnPublicShareLink } from '~/hooks/useCanViewAllVersionsOnPublicShareLink';

export const PublicAssetHeader = memo(() => {
  const { canViewAllVersions: canViewAssetVersions } = useCanViewAllVersionsOnPublicShareLink();

  const assetTitleContent = useMemo(() => <PublicAssetTitleBreadcrumbContent />, []);
  const { assetTitleBreadcrumbItem } = useAssetTitleBreadcrumbItem({ assetTitleContent });

  // A standalone clip link has no location to crumb — no collection is ever fetched here and no
  // board data is ever seeded — so the asset title is the whole trail.
  const breadcrumbItems = useMemo<BreadcrumbsItemType[]>(
    () => (assetTitleBreadcrumbItem ? [assetTitleBreadcrumbItem] : []),
    [assetTitleBreadcrumbItem],
  );

  return (
    <div className="flex flex-col">
      <PublicBanner />

      <Header
        headerLeft={
          <div className="flex items-center gap-2">
            {canViewAssetVersions && (
              <>
                <AssetModalVersionsButton />
                <AssetModalHeaderSeparator />
              </>
            )}
            <AssetModalBreadcrumbs breadcrumbItems={breadcrumbItems} />
          </div>
        }
        headerRight={
          <div className="flex items-center gap-2">
            <PublicOptionsMenu />
            <PublicAssetShareButton />
          </div>
        }
      />
    </div>
  );
});

PublicAssetHeader.displayName = 'PublicAssetHeader';
